import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HeaderAccent } from "@/lib/status";
import { ShieldCheck, CheckCircle2, AlertTriangle, XCircle, FileText } from "lucide-react";
import { MyDocuments, PendingDocs, PvgApplied } from "@/components/StaffDocuments";
import { ukDate } from "@/components/UkDateInput";

const TONE = {
  valid: "bg-sage-light text-sage-dark",
  expiring: "bg-gold-light text-gold-dark",
  expired: "bg-coral-light text-coral-dark",
  missing: "bg-slate-100 text-slate-500",
  pending: "bg-gold-light text-gold-dark",
};
const LABEL = { valid: "Valid", expiring: "Expiring", expired: "Expired", missing: "Not on file", pending: "Awaiting approval" };

function Item({ it }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-1.5 text-sm">
      <span className="truncate text-ink">{it.kind}</span>
      <span className="flex items-center gap-2">
        {it.applied_date && <span className="rounded-full bg-slateblue-light px-2 py-0.5 text-[11px] font-semibold text-slateblue">Applied {ukDate(it.applied_date)}</span>}
        {it.expiry && <span className="text-xs text-ink-muted">{ukDate(it.expiry)}</span>}
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[it.status]}`}>
          {LABEL[it.status]}{it.status === "expiring" && it.days != null ? ` · ${it.days}d` : ""}
        </span>
      </span>
    </div>
  );
}

function Avatar({ src, name }) {
  return src
    ? <img src={src} alt={name} className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-black/5" />
    : <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-light text-sm font-bold text-brand">{(name || "?").charAt(0)}</span>;
}

function ManagerView() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const { user } = useAuth();
  const canReview = ["admin", "office"].includes(user?.role);
  useEffect(() => { api.get("/compliance/overview").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3" data-testid="compliance-overview">
      {canReview && <PendingDocs />}
      {rows.length === 0 && <p className="text-ink-muted">No befrienders found.</p>}
      {rows.map((c) => {
        const clear = c.expired + c.expiring + c.missing === 0;
        return (
          <div key={c.carer_id} data-testid={`compliance-row-${c.carer_id}`} className="rounded-xl border border-border bg-white p-4 shadow-sm">
            <button onClick={() => setOpenId(openId === c.carer_id ? null : c.carer_id)} className="flex w-full items-center gap-3 text-left">
              <Avatar src={c.photo} name={c.carer_name} />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-ink">{c.carer_name}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {c.expired > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-coral-light px-2 py-0.5 text-[11px] font-semibold text-coral-dark"><XCircle size={11} /> {c.expired} expired</span>}
                  {c.expiring > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-gold-light px-2 py-0.5 text-[11px] font-semibold text-gold-dark"><AlertTriangle size={11} /> {c.expiring} expiring</span>}
                  {c.missing > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{c.missing} not on file</span>}
                  {c.pending > 0 && <span className="rounded-full bg-gold-light px-2 py-0.5 text-[11px] font-semibold text-gold-dark">{c.pending} awaiting approval</span>}
                  {c.items.some((it) => it.applied_date) && <span data-testid={`pvg-on-way-${c.carer_id}`} className="rounded-full bg-slateblue-light px-2 py-0.5 text-[11px] font-semibold text-slateblue">PVG on the way</span>}
                  {clear && <span className="inline-flex items-center gap-1 rounded-full bg-sage-light px-2 py-0.5 text-[11px] font-semibold text-sage-dark"><CheckCircle2 size={11} /> All in order</span>}
                </div>
              </div>
            </button>
            {openId === c.carer_id && (
              <div className="mt-3 grid gap-1.5 border-t border-border pt-3 sm:grid-cols-2">
                {c.items.map((it, i) => <Item key={i} it={it} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CarerView() {
  const [data, setData] = useState(null);
  const load = () => api.get("/compliance/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <div className="flex h-48 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" /></div>;
  const reqUnread = data.policies.filter((p) => p.required && !p.read);
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="compliance-me">
      <MyDocuments onChanged={load} />
      {(() => { const pvg = data.items.find((it) => it.kind.startsWith("PVG")); return pvg && ["missing", "expired"].includes(pvg.status) ? <PvgApplied item={pvg} onChanged={load} /> : null; })()}
      <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><FileText size={18} className="text-brand" /> My documents</h2>
        <div className="space-y-1.5">{data.items.map((it, i) => <Item key={i} it={it} />)}</div>
      </div>
      <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><ShieldCheck size={18} className="text-brand" /> Policy reading</h2>
        {reqUnread.length > 0 && (
          <p className="mb-3 rounded-lg bg-coral-light px-3 py-2 text-sm font-medium text-coral-dark">{reqUnread.length} required polic{reqUnread.length === 1 ? "y" : "ies"} still to read &amp; accept.</p>
        )}
        <div className="space-y-1.5">
          {data.policies.map((p) => (
            <div key={p.id} data-testid={`me-policy-${p.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-1.5 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                {p.read ? <CheckCircle2 size={14} className="shrink-0 text-sage-dark" /> : <AlertTriangle size={14} className="shrink-0 text-gold-dark" />}
                <span className="truncate text-ink">{p.title}</span>
                {p.required && <span className="shrink-0 rounded-full bg-coral-light px-1.5 py-0.5 text-[10px] font-bold uppercase text-coral-dark">Must read</span>}
              </span>
              <span className={`shrink-0 text-xs font-semibold ${p.read ? "text-sage-dark" : "text-gold-dark"}`}>{p.read ? "Accepted" : "Unread"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Compliance() {
  const { user } = useAuth();
  const isManager = ["admin", "office", "viewer"].includes(user?.role);
  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-bold tracking-tight text-ink"><ShieldCheck size={28} className="text-brand" /> {isManager ? "Compliance dashboard" : "My compliance"}</h1>
        <p className="mt-1 text-ink-soft">{isManager ? "Befrienders with missing or expiring documents — most urgent first." : "Your documents and policy reading in one place."}</p>
        <HeaderAccent />
      </div>
      {isManager ? <ManagerView /> : <CarerView />}
    </div>
  );
}
